import { getDistance } from '@shared/helpers/geometry/coordinates.helpers';
import type { Point2D } from '@shared/models/geometry.model';

export type SnapShapeTranslationToStoreyBelowInput = {
  /** Vertices of the shape being translated */
  shapePoints: readonly Point2D[];
  /** Shapes on the storey below to snap against */
  storeyBelowShapes: readonly { points?: readonly Point2D[] }[];
  /** Proposed translation before snap */
  delta: Point2D;
  /** Max distance to a storey-below vertex that still snaps */
  pointThreshold: number;
  /** Max distance to a storey-below edge that still snaps */
  lineThreshold: number;
};

/**
 * Adjust a shape-translation delta so a moved vertex lands on a point or
 * edge of the storey below. Point snaps win over line snaps when both are
 * within their thresholds. Returns {@link SnapShapeTranslationToStoreyBelowInput.delta}
 * when nothing is close enough.
 */
export const snapShapeTranslationToStoreyBelowGeometry = (
  input: SnapShapeTranslationToStoreyBelowInput,
): Point2D => {
  const {
    shapePoints,
    storeyBelowShapes,
    delta,
    pointThreshold,
    lineThreshold,
  } = input;
  if (!shapePoints.length || !storeyBelowShapes.length) return delta;

  const [dx, dy] = delta;
  let bestPoint: { distance: number; delta: Point2D } | undefined;
  let bestLine: { distance: number; delta: Point2D } | undefined;

  for (const point of shapePoints) {
    const movedPoint: Point2D = [point[0] + dx, point[1] + dy];
    for (const belowShape of storeyBelowShapes) {
      const belowPoints = belowShape.points ?? [];
      for (const belowPoint of belowPoints) {
        const distance = getDistance(movedPoint, belowPoint);
        if (distance >= pointThreshold) continue;
        if (bestPoint && distance >= bestPoint.distance) continue;
        bestPoint = {
          distance,
          delta: [belowPoint[0] - point[0], belowPoint[1] - point[1]],
        };
      }

      if (belowPoints.length < 2) continue;
      const total = belowPoints.length;
      for (let i = 0; i < total; i += 1) {
        const start = belowPoints[i];
        const end = belowPoints[(i + 1) % total];
        if (!start || !end) continue;
        const t = segmentProjectionT(movedPoint, start, end);
        if (t === undefined || t < 0 || t > 1) continue;
        const projected: Point2D = [
          start[0] + (end[0] - start[0]) * t,
          start[1] + (end[1] - start[1]) * t,
        ];
        const distance = getDistance(movedPoint, projected);
        if (distance >= lineThreshold) continue;
        if (bestLine && distance >= bestLine.distance) continue;
        bestLine = {
          distance,
          delta: [projected[0] - point[0], projected[1] - point[1]],
        };
      }
    }
  }

  // Same priority as vertex drag: any in-range point beats any in-range line.
  if (bestPoint) return bestPoint.delta;
  if (bestLine) return bestLine.delta;
  return delta;
};

/**
 * Unclamped projection ratio of {@link point} onto segment AB.
 * `undefined` when the segment has zero length.
 */
const segmentProjectionT = (
  point: Point2D,
  segmentStart: Point2D,
  segmentEnd: Point2D,
): number | undefined => {
  const abx = segmentEnd[0] - segmentStart[0];
  const aby = segmentEnd[1] - segmentStart[1];
  const abLengthSquared = abx * abx + aby * aby;
  if (abLengthSquared === 0) return undefined;
  const apx = point[0] - segmentStart[0];
  const apy = point[1] - segmentStart[1];
  return (apx * abx + apy * aby) / abLengthSquared;
};
